import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { logEvent, logEventDebounced } from "../utils/logger";
import { getApiBase } from "../apiBase";
import {
  FaUpload,
  FaFileAlt,
  FaTrash,
  FaGlobe,
  FaSearch,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaCheck,
  FaClock,
  FaExclamationTriangle,
  FaTimes,
  FaFileExcel,
  FaFilePdf,
  FaFileWord,
  FaBook,
  FaEye,
  FaSync
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

const normalizeStatus = (status, isTemp) => {
  if (isTemp) return "processing";
  if (!status) return "processing";
  switch (status.toLowerCase()) {
    case "completed":
    case "indexed":
    case "ready":
    case "active":
      return "active";
    case "processing":
    case "queued":
    case "in_progress":
      return "processing";
    case "failed":
      return "failed";
    default:
      return "processing";
  }
};

const normalizeKey = (name = "") =>
  name
    .toLowerCase()
    .split("#")[0]
    .split("?")[0]
    .replace(/https?:\/\//g, "")
    .replace(/www\./g, "")
    .replace(/_cleaned/g, "")
    .replace(/\.(pdf|txt|md|docx|doc|csv|json|xml|html)$/g, "")
    .replace(/\d{8,}/g, "")
    .replace(/[_\s-]+/g, "")
    .replace(/[^a-z0-9]/g, "");

const STATUS_META = {
  active: { label: "Active", color: "#1F8A4C" },
  processing: { label: "Processing", color: "#B8860B" },
  failed: { label: "Failed", color: "#C0392B" }
};

const StatusMark = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.processing;
  return (
    <span className="relative inline-flex items-center group/status flex-shrink-0">
      {status === "processing" ? (
        <span className="w-3.5 h-3.5 border-[1.5px] border-t-[#B8860B] border-r-[#B8860B] border-b-transparent border-l-transparent rounded-full animate-spin" />
      ) : status === "failed" ? (
        <FaExclamationTriangle className="text-[13px]" style={{ color: meta.color }} />
      ) : (
        <FaCheck className="text-[12px]" style={{ color: meta.color }} />
      )}
      <span
        className="pointer-events-none absolute left-1/2 -translate-x-1/2 -top-7 whitespace-nowrap px-2 py-1 text-[10px] font-medium text-white bg-[#2F1B2C] rounded-[3px] opacity-0 group-hover/status:opacity-100 transition-opacity duration-150 z-10"
      >
        {meta.label}
      </span>
    </span>
  );
};

const fileTypeLabel = (type) => (type === "md" ? "Scraped page" : (type || "").toUpperCase());

const getCurrentUserName = () => localStorage.getItem("fullname") || "You";

const getFileIcon = (file) => {
  if (file.file_type === "md") return <FaGlobe className="text-[13px] text-[#6E4466]" />;
  if (["xls", "xlsx"].includes(file.file_type)) return <FaFileExcel className="text-[13px] text-[#6E4466]" />;
  if (file.file_type === "pdf") return <FaFilePdf className="text-[13px] text-[#6E4466]" />;
  if (file.file_type === "docx" || file.file_type === "doc") return <FaFileWord className="text-[13px] text-[#6E4466]" />;
  return <FaFileAlt className="text-[13px] text-[#6E4466]" />;
};

const FileRow = ({ file, isSelected, onSelect, onView, onDelete, formatDate }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="border-b border-gray-200">
      <div
        className="flex items-center gap-3 py-3 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => {
            e.stopPropagation();
            onSelect(e.target.checked);
          }}
          onClick={(e) => e.stopPropagation()}
          className="w-3.5 h-3.5 rounded-none border-gray-400 text-[#6E4466] focus:ring-0 flex-shrink-0"
        />

        {getFileIcon(file)}

        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 break-words">
            {file.display_name || file.file_name}
          </p>

          {file.file_type === "md" && (
            <p className="text-[11px] text-gray-400 mt-0.5">
              {file.file_name
                ?.match(/^(.+?)_(com|org|net|in|co|io)_/)?.[0]
                ?.replace(/_$/, "")
                ?.replace(/_(com|org|net|in|co|io)$/, ".$1")
                ?.replace(/_/g, ".")}
            </p>
          )}
        </div>

        <StatusMark status={file.status} />

        <div className="flex items-center gap-3 flex-shrink-0 pl-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onView(file);
            }}
            className="text-gray-400 hover:text-[#6E4466] transition"
            title="View"
          >
            <FaEye className="text-[13px]" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(file);
            }}
            className="text-gray-400 hover:text-red-600 transition"
            title="Delete"
          >
            <FaTrash className="text-[13px]" />
          </button>
          <FaChevronDown
            className={`text-gray-300 text-[10px] transition-transform ${isExpanded ? "rotate-180" : ""}`}
          />
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="pb-3 pl-6 flex flex-wrap gap-x-8 gap-y-1.5 text-xs">
              <div className="flex items-center gap-1.5">
                <span className="text-gray-400">Uploaded at</span>
                <span className="text-gray-700">{formatDate(file.created_at)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-400">Type</span>
                <span className="text-gray-700">{fileTypeLabel(file.file_type)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-gray-400">By</span>
                <span className="text-gray-700">{file.uploaded_by || getCurrentUserName()}</span>
              </div>
              {file.source_url && (
                <div className="flex items-center gap-1.5 min-w-0 basis-full">
                  <span className="text-gray-400 flex-shrink-0">Source</span>
                  <span className="text-gray-700 break-all">{file.source_url}</span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const KnowledgeAfterOnboard = () => {
  const { tenantId, botId } = useParams();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [fileMeta, setFileMeta] = useState(null);
  const [showSitemapModal, setShowSitemapModal] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [sitemapUrls, setSitemapUrls] = useState([]);
  const [selectedUrls, setSelectedUrls] = useState([]);
  const [loadingUrls, setLoadingUrls] = useState(false);
  const [scrapingSelected, setScrapingSelected] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [botName, setBotName] = useState("Your Agent");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const fileInputRef = useRef(null);
  const pollIntervalsRef = useRef({});
  const [filesAwaitingConfirmation, setFilesAwaitingConfirmation] = useState(new Set());
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [showStorageModal, setShowStorageModal] = useState(false);
  const [storageLimitError, setStorageLimitError] = useState(null);
  const [resyncLoading, setResyncLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const openInNewTab = useCallback((url) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchBotDetails = async () => {
    try {
      const res = await fetch(`${getApiBase()}/tenant/${tenantId}/bot/${botId}/details`);
      if (!res.ok) throw new Error("Failed to fetch bot details");
      const json = await res.json();
      setBotName(json?.data?.bot_name || "Your Agent");
    } catch (err) {
      console.error("Bot name fetch failed", err);
      setBotName("Your Bot");
    }
  };

  const bytesToGB = (bytes = 0) => Number((bytes / (1024 * 1024 * 1024)).toFixed(2));

  const formatDate = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "—";
    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    });
  };

  const isSameScrapedFile = (tempFile, backendFile) => {
    if (!tempFile?.source_url || !backendFile?.file_name) return false;
    const backendKey = normalizeKey(backendFile.file_name);
    const urlKey = normalizeKey(
      tempFile.source_url
        .split("#")[0]
        .split("?")[0]
        .replace(/^https?:\/\//, "")
        .replace(/^www\./, "")
    );
    return backendKey.includes(urlKey);
  };

  const isDuplicateScrapeResponse = (data) => {
    if (!data) return false;
    if (Array.isArray(data.duplicates) && data.duplicates.length > 0) return true;
    if (data.status === "duplicates_only") return true;
    if (data.pages_to_crawl === 0 && data.validated_pages === 0) return true;
    return false;
  };

  const isSameLogicalFile = (tempName, backendName) => {
    if (!tempName || !backendName) return false;
    const cleanTemp = normalizeKey(tempName);
    const cleanBackend = normalizeKey(backendName);
    return cleanBackend === cleanTemp || cleanBackend.startsWith(cleanTemp);
  };

  const fetchKnowledgeBase = async () => {
    logEventDebounced("KB_FETCH_START", { tenantId, botId }, 800);
    try {
      const res = await fetch(`${getApiBase()}/knowledge-base?tenant_id=${tenantId}&bot_id=${botId}`);
      const data = await res.json();
      logEventDebounced("KB_FETCH_SUCCESS", { count: (data.files || []).length, tenantId, botId }, 800);

      const normalizedBackendFiles = (data.files || [])
        .filter(f => f.status !== "deleted")
        .map(f => ({
          ...f,
          status: normalizeStatus(f.status, false),
          uploaded_by: f.uploaded_by || f.created_by || f.user_name || getCurrentUserName(),
          _temp: false,
        }));

      setFilesAwaitingConfirmation(prevSet => {
        const newSet = new Set(prevSet);
        normalizedBackendFiles.forEach(backendFile => {
          prevSet.forEach(tempKey => {
            const tempFile = files.find(f => normalizeKey(f.file_name) === tempKey);
            if (
              (tempFile?.source_url && isSameScrapedFile(tempFile, backendFile)) ||
              (!tempFile?.source_url && isSameLogicalFile(tempFile?.file_name, backendFile.file_name))
            ) {
              newSet.delete(tempKey);
            }
          });
        });
        return newSet;
      });

      setFiles(prev => {
        const map = {};
        normalizedBackendFiles.forEach(f => {
          const key = normalizeKey(f.file_name);
          map[key] = f;
        });
        prev.forEach(tempFile => {
          if (!tempFile._temp) return;
          const tempKey = normalizeKey(tempFile.file_name);
          const stillProcessing = !normalizedBackendFiles.some(backendFile =>
            tempFile.source_url
              ? isSameScrapedFile(tempFile, backendFile)
              : isSameLogicalFile(tempFile.file_name, backendFile.file_name)
          );
          if (stillProcessing) {
            map[tempKey] = tempFile;
          }
        });
        return Object.values(map);
      });
    } catch (e) {
      logEventDebounced("KB_FETCH_FAILED", { error: e.message }, 800);
      showToast("Failed to load Knowledge Base", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    logEvent("KB_PAGE_VIEW", { tenantId, botId });
  }, [tenantId, botId]);

  useEffect(() => {
    if (filesAwaitingConfirmation.size === 0) return;
    const interval = setInterval(fetchKnowledgeBase, 3000);
    return () => clearInterval(interval);
  }, [filesAwaitingConfirmation.size]);

  useEffect(() => {
    if (query.trim().length > 0) {
      logEventDebounced("KB_SEARCH", { query }, 600);
    }
  }, [query]);

  useEffect(() => {
    logEvent("KB_STATUS_FILTER_CHANGED", { value: statusFilter });
  }, [statusFilter]);

  useEffect(() => {
    logEvent("KB_TYPE_FILTER_CHANGED", { value: typeFilter });
  }, [typeFilter]);

  useEffect(() => {
    logEvent("KB_FILTER_TOGGLED", { isOpen: isFilterOpen });
  }, [isFilterOpen]);

  useEffect(() => {
    logEvent("KB_PAGE_CHANGED", { page: currentPage });
  }, [currentPage]);

  useEffect(() => {
    fetchKnowledgeBase();
    fetchBotDetails();
    return () => {
      Object.values(pollIntervalsRef.current).forEach(clearInterval);
    };
  }, [tenantId, botId]);

  const handleFileUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files || []);
    if (!uploadedFiles.length) return;
    setShowUploadModal(false);
    logEventDebounced("KB_UPLOAD_STARTED", { totalFiles: uploadedFiles.length }, 500);

    const allowedExtensions = ["pdf", "txt", "xlsx", "xls", "docx", "md"];
    for (const f of uploadedFiles) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        showToast(`Unsupported format: ${f.name}`, "error");
        logEventDebounced("KB_UPLOAD_REJECTED", { fileName: f.name, reason: "Invalid extension" }, 500);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (f.size > 10 * 1024 * 1024) {
        showToast(`File too large: ${f.name} (max 10MB)`, "error");
        logEventDebounced("KB_UPLOAD_REJECTED", { fileName: f.name, reason: "File too large" }, 500);
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    const newOptimisticFiles = uploadedFiles.map(f => ({
      file_id: `temp_upload_${Date.now()}_${f.name}`,
      file_name: f.name,
      file_type: f.name.split('.').pop().toLowerCase(),
      status: "processing",
      created_at: new Date().toISOString(),
      uploaded_by: getCurrentUserName(),
      _temp: true
    }));

    setFiles(prev => [...prev, ...newOptimisticFiles]);
    setFilesAwaitingConfirmation(prevSet => {
      const newSet = new Set(prevSet);
      newOptimisticFiles.forEach(f => {
        newSet.add(normalizeKey(f.file_name));
      });
      return newSet;
    });

    for (const f of uploadedFiles) {
      logEventDebounced("KB_FILE_UPLOAD_INITIATED", { fileName: f.name }, 500);
      const fd = new FormData();
      fd.append("file", f);
      try {
        const res = await fetch(`${getApiBase()}/documents/ingest?tenant_id=${tenantId}&bot_id=${botId}`, {
          method: "POST",
          body: fd
        });
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          logEventDebounced("KB_FILE_UPLOAD_FAILED", { fileName: f.name, status: res.status }, 500);
          if (res.status === 403) {
            setStorageLimitError(errorData);
            setShowStorageModal(true);
            setFiles(prev => prev.filter(file => file.file_name !== f.name));
            setFilesAwaitingConfirmation(prev => {
              const newSet = new Set(prev);
              newSet.delete(normalizeKey(f.name));
              return newSet;
            });
            return;
          }
          if (res.status === 413) {
            setStorageLimitError({
              reason: "FILE_TOO_LARGE",
              message: errorData.detail || "File exceeds allowed size."
            });
            setShowStorageModal(true);
            setFiles(prev => prev.filter(file => file.file_name !== f.name));
            setFilesAwaitingConfirmation(prev => {
              const newSet = new Set(prev);
              newSet.delete(normalizeKey(f.name));
              return newSet;
            });
            return;
          }
        } else {
          logEventDebounced("KB_FILE_UPLOAD_SUCCESS", { fileName: f.name }, 500);
        }
      } catch (err) {
        logEventDebounced("KB_FILE_UPLOAD_EXCEPTION", { fileName: f.name, error: err.message }, 500);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleResync = async () => {
    setResyncLoading(true);
    try {
      const res = await fetch(
        `${getApiBase()}/kb/resync?tenant_id=${tenantId}&bot_id=${botId}`,
        { method: "POST" }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || data.message || "Resync failed");
      }
      showToast(data.message || "Knowledge base synced");
      fetchKnowledgeBase();
    } catch (e) {
      showToast(e.message || "Resync failed", "error");
    } finally {
      setResyncLoading(false);
    }
  };

  const fetchSitemapPages = async () => {
    if (!websiteUrl) return showToast("Enter website URL", "error");
    try {
      setLoadingUrls(true);
      const res = await fetch(`${getApiBase()}/sitemap-pages?tenant_id=${tenantId}&bot_id=${botId}&url=${encodeURIComponent(websiteUrl)}`);
      const data = await res.json();
      if (data.success) {
        setSitemapUrls(data.pages || []);
      } else {
        showToast("Failed to load pages", "error");
      }
    } catch {
      showToast("Error fetching sitemap URLs", "error");
    } finally {
      setLoadingUrls(false);
    }
  };

  const handleCloseSitemapModal = () => {
    setShowSitemapModal(false);
    setWebsiteUrl("");
    setSitemapUrls([]);
    setSelectedUrls([]);
    setLoadingUrls(false);
  };

  const handleScrapeSelected = async () => {
    if (!selectedUrls.length) {
      showToast("Select at least one page", "error");
      return;
    }
    if (scrapingSelected) return;
    setScrapingSelected(true);

    const newOptimisticFiles = selectedUrls.map((url, i) => {
      const parsed = new URL(url);
      const host = parsed.hostname.replace(/\./g, "_");
      const path = parsed.pathname.replace(/\//g, "_");
      return {
        file_id: `temp_scrape_${Date.now()}_${i}`,
        _temp: true,
        file_name: `${host}${path}`,
        source_url: url,
        file_type: "md",
        status: "processing",
        created_at: new Date().toISOString(),
        uploaded_by: getCurrentUserName()
      };
    });

    setFiles(prev => [...prev, ...newOptimisticFiles]);
    setFilesAwaitingConfirmation(prevSet => {
      const newSet = new Set(prevSet);
      newOptimisticFiles.forEach(f => newSet.add(normalizeKey(f.file_name)));
      return newSet;
    });
    handleCloseSitemapModal();
    try {
      const res = await fetch(`${getApiBase()}/scrape-selected-pages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenant_id: tenantId, bot_id: botId, pages: selectedUrls })
      });
      const data = await res.json();

      if (isDuplicateScrapeResponse(data)) {
        const names = (data.duplicates || data.pages || [])
          .map(d => d.title || d.expected_filename || d.url)
          .filter(Boolean);
        const msg = names.length ? `Already exists: ${names.join(", ")}` : "Already exist in the Knowledge Base.";
        showToast(msg, "error");
        setFiles(prev => prev.filter(f => !newOptimisticFiles.some(opt => opt.file_id === f.file_id)));
        setFilesAwaitingConfirmation(prev => {
          const newSet = new Set(prev);
          newOptimisticFiles.forEach(f => newSet.delete(normalizeKey(f.file_name)));
          return newSet;
        });
        return;
      }

      if (!res.ok) {
        throw new Error(data.detail || data.message || "Failed to start scraping");
      }
      showToast("Scraping started");
    } catch (e) {
      showToast(e.message, "error");
      setFiles(prev =>
        prev.map(file =>
          newOptimisticFiles.some(opt => opt.file_id === file.file_id)
            ? { ...file, status: "failed" }
            : file
        )
      );
    } finally {
      setScrapingSelected(false);
    }
  };

  const handleFileClick = async (file) => {
    logEventDebounced("KB_PREVIEW_OPEN", { fileName: file.file_name, type: file.file_type, status: file.status }, 400);
    setSelectedFile(file);
    setFilePreviewUrl(null);
    setPreviewError(null);
    setPreviewLoading(true);
    setFileMeta(null);
    try {
      const res = await fetch(`${getApiBase()}/documents/file-url?tenant_id=${tenantId}&bot_id=${botId}&file_name=${encodeURIComponent(file.file_name)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail?.message || "Failed to fetch file URL");
      setFilePreviewUrl(data.presigned_url);
      setFileMeta({
        fileName: data.file_name,
        fileType: data.file_type,
        fileSize: data.file_size,
        uploadedAt: data.metadata?.created_at,
        bucket: data.metadata?.s3bucket_name,
        prefix: data.metadata?.prefix,
        generatedAt: data.generated_at_utc
      });
      logEventDebounced("KB_PREVIEW_SUCCESS", { fileName: file.file_name }, 400);
    } catch (error) {
      setPreviewError(error.message);
      logEventDebounced("KB_PREVIEW_FAILED", { fileName: file.file_name, error: error.message }, 400);
      showToast(error.message, "error");
    } finally {
      setPreviewLoading(false);
    }
  };

  const normalizedFiles = useMemo(() => {
    return files.map((f) => ({
      ...f,
      created_at_ts: f._temp
        ? Date.now() + 1000000
        : f.created_at
          ? new Date(f.created_at).getTime()
          : 0,
    }));
  }, [files]);

  const totalStorageBytes = useMemo(() => {
    return files
      .filter(f => !f._temp && f.status === "active")
      .reduce((sum, f) => sum + (f.file_size || 0), 0);
  }, [files]);

  useEffect(() => {
    const storageGB = bytesToGB(totalStorageBytes);
    localStorage.setItem(`tenant_storage_used_${tenantId}`, String(storageGB));
    logEventDebounced("KB_STORAGE_UPDATED", { tenantId, botId, storage_gb: storageGB }, 800);
  }, [totalStorageBytes, tenantId, botId]);

  const filteredFiles = useMemo(() => {
    return normalizedFiles
      .filter((f) => {
        if (statusFilter !== "all" && f.status !== statusFilter) return false;
        if (typeFilter !== "all" && f.file_type !== typeFilter) return false;
        if (query.trim()) {
          const q = query.toLowerCase();
          return (f.file_name || "").toLowerCase().includes(q);
        }
        return true;
      })
      .sort((a, b) => b.created_at_ts - a.created_at_ts);
  }, [normalizedFiles, statusFilter, typeFilter, query]);

  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedFiles = filteredFiles.slice(startIndex, startIndex + itemsPerPage);

  const handleDelete = async () => {
    if (!selectedFiles || selectedFiles.size === 0) {
      showToast("No documents selected", "error");
      return;
    }
    setDeleteLoading(true);
    try {
      const names = Array.from(selectedFiles);
      for (const file_name of names) {
        const res = await fetch(`${getApiBase()}/documents/${encodeURIComponent(file_name)}?tenant_id=${tenantId}&bot_id=${botId}`, {
          method: "DELETE"
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Delete failed");
        }
      }
      setFiles(prev => prev.filter(f => !selectedFiles.has(f.file_name)));
      setSelectedFiles(new Set());
      setShowDeleteModal(false);
      showToast("Selected documents deleted");
    } catch {
      showToast("Delete failed", "error");
    }
    setDeleteLoading(false);
  };

  const uploadedCount = files.filter((f) => f.file_type !== "md" && f.status !== "deleted").length;
  const scrapedCount = files.filter((f) => f.file_type === "md" && f.status !== "deleted").length;
  const activeCount = files.filter((f) => f.status === "active").length;
  const processingCount = files.filter((f) => f.status === "processing").length;
  const failedCount = files.filter((f) => f.status === "failed").length;
  const storageGB = bytesToGB(totalStorageBytes);

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center items-center mt-6 gap-1.5">
        <button
          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          disabled={currentPage === 1}
          className={`px-3 py-1.5 text-xs font-medium border ${currentPage === 1
              ? "text-gray-300 border-gray-200 cursor-not-allowed"
              : "text-gray-600 border-gray-300 hover:border-[#6E4466] hover:text-[#6E4466]"
            }`}
        >
          Prev
        </button>

        {startPage > 1 && (
          <>
            <button
              onClick={() => setCurrentPage(1)}
              className="w-8 h-8 text-xs font-medium border border-gray-300 text-gray-600 hover:border-[#6E4466] hover:text-[#6E4466]"
            >
              1
            </button>
            {startPage > 2 && <span className="px-1 text-gray-300 text-xs">…</span>}
          </>
        )}

        {pageNumbers.map((num) => (
          <button
            key={num}
            onClick={() => setCurrentPage(num)}
            className={`w-8 h-8 text-xs font-medium border ${
              currentPage === num
                ? "bg-[#6E4466] text-white border-[#6E4466]"
                : "text-gray-600 border-gray-300 hover:border-[#6E4466] hover:text-[#6E4466]"
            }`}
          >
            {num}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="px-1 text-gray-300 text-xs">…</span>}
            <button
              onClick={() => setCurrentPage(totalPages)}
              className="w-8 h-8 text-xs font-medium border border-gray-300 text-gray-600 hover:border-[#6E4466] hover:text-[#6E4466]"
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
          disabled={currentPage === totalPages}
          className={`px-3 py-1.5 text-xs font-medium border ${currentPage === totalPages
              ? "text-gray-300 border-gray-200 cursor-not-allowed"
              : "text-gray-600 border-gray-300 hover:border-[#6E4466] hover:text-[#6E4466]"
            }`}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white text-gray-800 w-full" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <div className="w-full mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <AnimatePresence>
          {toast && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`fixed top-6 right-6 z-50 px-4 py-2 text-sm font-medium text-white flex items-center gap-2 border-l-4 bg-[#2F1B2C] ${
                toast.type === "error" ? "border-red-500" : "border-green-500"
              }`}
            >
              {toast.type === "error" ? <FaExclamationTriangle className="text-red-400 text-xs" /> : <FaCheck className="text-green-400 text-xs" />}
              {toast.message}
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showSitemapModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
              onClick={handleCloseSitemapModal}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-2xl border border-gray-200 flex flex-col max-h-[85vh]"
              >
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-base font-semibold text-[#2F1B2C]">Scrape website</h2>
                  <p className="text-xs text-gray-500 mt-1">Paste a website URL and select pages to scrape</p>
                </div>
                <div className="px-6 py-4 flex gap-2 border-b border-gray-200">
                  <input
                    type="url"
                    placeholder="https://example.com"
                    value={websiteUrl}
                    onChange={(e) => setWebsiteUrl(e.target.value)}
                    className="flex-1 border-b border-gray-300 px-1 py-2 text-sm focus:border-[#6E4466] outline-none bg-transparent"
                  />
                  <button
                    onClick={fetchSitemapPages}
                    disabled={loadingUrls}
                    className="px-5 py-2 border border-[#6E4466] text-[#6E4466] text-sm font-medium hover:bg-[#6E4466] hover:text-white transition"
                  >
                    {loadingUrls ? "Loading…" : "Search"}
                  </button>
                </div>
                {sitemapUrls.length > 0 && (
                  <div className="px-6 py-2 flex justify-between items-center border-b border-gray-200">
                    <label className="flex items-center gap-2 text-xs font-medium text-[#2F1B2C]">
                      <input
                        type="checkbox"
                        checked={selectedUrls.length === sitemapUrls.length}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedUrls(sitemapUrls);
                          } else {
                            setSelectedUrls([]);
                          }
                        }}
                      />
                      Select all
                    </label>
                    <span className="text-[11px] text-gray-400">{selectedUrls.length} selected</span>
                  </div>
                )}
                <div className="flex-1 overflow-auto px-6 py-3 text-sm">
                  {sitemapUrls.length === 0 && !loadingUrls && (
                    <div className="text-gray-400 text-center py-10 text-xs">No pages loaded</div>
                  )}
                  {sitemapUrls.map((url, idx) => (
                    <label
                      key={idx}
                      className="flex items-start gap-3 py-2 border-b border-gray-100 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedUrls.includes(url)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelectedUrls((prev) =>
                            checked ? [...prev, url] : prev.filter((u) => u !== url)
                          );
                        }}
                        className="mt-1"
                      />
                      <span className="break-all text-gray-700 text-xs leading-relaxed">{url}</span>
                    </label>
                  ))}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end items-center gap-3">
                  <button
                    onClick={handleCloseSitemapModal}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleScrapeSelected}
                    disabled={scrapingSelected || selectedUrls.length === 0}
                    className="px-6 py-2 bg-[#6E4466] text-white text-sm font-medium hover:bg-[#54334E] transition disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {scrapingSelected ? "Scraping…" : `Scrape ${selectedUrls.length} page${selectedUrls.length !== 1 ? "s" : ""}`}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showDeleteModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[999] bg-black/50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-white max-w-md w-full border border-gray-200 p-5"
              >
                <h3 className="text-base font-semibold text-[#2F1B2C]">Delete document{selectedFiles.size > 1 ? "s" : ""}</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Are you sure you want to delete {selectedFiles.size} document{selectedFiles.size > 1 ? "s" : ""}? This cannot be undone.
                </p>
                <div className="flex justify-end gap-3 mt-5">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleteLoading}
                    className={`px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 transition ${deleteLoading ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleteLoading}
                    className={`px-4 py-1.5 text-sm text-white flex items-center gap-2 bg-red-600 hover:bg-red-700 transition ${deleteLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {deleteLoading ? (
                      <>
                        <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Deleting…
                      </>
                    ) : (
                      "Delete"
                    )}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showStorageModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-white p-6 w-full max-w-md mx-4 border border-gray-200"
              >
                <div className="text-center">
                  <h3 className="text-base font-semibold text-[#2F1B2C] mb-2">Storage limit reached</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    {storageLimitError?.reason === "INSUFFICIENT_STORAGE"
                      ? `Only ${storageLimitError?.remaining_mb} MB remaining.`
                      : storageLimitError?.message || "Upload limit exceeded."}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => setShowStorageModal(false)}
                      className="flex-1 border border-gray-300 text-gray-700 py-2 text-sm font-medium hover:bg-gray-50 transition"
                    >
                      Close
                    </button>
                    <button
                      onClick={() => navigate(`/billing/${tenantId}`)}
                      className="flex-1 bg-[#6E4466] text-white py-2 text-sm font-medium hover:bg-[#54334E] transition"
                    >
                      Upgrade plan
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {selectedFile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50"
              onClick={() => setSelectedFile(null)}
            >
              <div className="absolute inset-0 bg-black/50" />
              <motion.div
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ type: "spring", stiffness: 260, damping: 25 }}
                onClick={(e) => e.stopPropagation()}
                className="absolute right-0 top-0 h-full w-full sm:w-[50%] bg-white border-l border-gray-200 flex flex-col"
              >
                {fileMeta && (
                  <div className="px-7 py-5 border-b border-gray-200">
                    <div className="flex justify-between items-start gap-6">
                      <div className="flex items-start gap-3">
                        <FaFileAlt className="text-[#6E4466] text-base mt-1" />
                        <div className="space-y-1 max-w-[320px]">
                          <h3 className="text-sm font-semibold text-[#2F1B2C] break-all leading-snug">{fileMeta.fileName}</h3>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                            <span className="uppercase tracking-wide">{fileMeta.fileType}</span>
                            <span>· {(fileMeta.fileSize / 1024).toFixed(2)} KB</span>
                            <span>· {formatDate(fileMeta.uploadedAt)}</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedFile(null)}
                        className="text-gray-400 hover:text-[#6E4466] transition"
                        aria-label="Close"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex-1 p-5 overflow-hidden">
                  {previewLoading && (
                    <div className="h-full flex flex-col items-center justify-center gap-3 text-[#6E4466]/80">
                      <div className="h-8 w-8 border-2 border-gray-200 border-t-[#6E4466] rounded-full animate-spin" />
                      <p className="text-xs font-medium tracking-wide">Loading preview…</p>
                    </div>
                  )}
                  {!previewLoading && previewError && (
                    <div className="h-full flex items-center justify-center">
                      <div className="px-6 py-4 border border-red-200 text-sm text-red-600">
                        {previewError}
                      </div>
                    </div>
                  )}
                  {!previewLoading && !previewError && filePreviewUrl && (
                    <div className="h-full border border-gray-200 overflow-hidden">
                      {selectedFile.file_type === "pdf" ? (
                        <iframe
                          src={`${filePreviewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                          title={selectedFile.file_name}
                          className="w-full h-full bg-white"
                        />
                      ) : (
                        <div className="h-full flex flex-col items-center justify-center text-center p-10">
                          <FaFileAlt className="text-2xl text-[#6E4466] mb-4" />
                          <p className="text-sm font-medium text-[#6E4466] mb-2">Preview not available</p>
                          <p className="text-xs text-gray-500 mb-5">
                            {selectedFile.file_type.toUpperCase()} files can't be previewed here.
                          </p>
                          <a
                            href={filePreviewUrl}
                            download={selectedFile.file_name}
                            className="inline-flex items-center gap-2 px-5 py-2 border border-[#6E4466] text-[#6E4466] text-sm font-medium hover:bg-[#6E4466] hover:text-white transition"
                          >
                            Download file
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showUploadModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center p-4"
              onClick={() => setShowUploadModal(false)}
            >
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-lg border border-gray-200"
              >
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-base font-semibold text-[#2F1B2C]">Upload files</h2>
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition"
                  >
                    <FaTimes className="text-base" />
                  </button>
                </div>

                <div className="p-6">
                  <div
                    className="border border-dashed border-gray-300 p-8 text-center cursor-pointer hover:border-[#6E4466] transition"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FaUpload className="text-[#6E4466] text-xl mx-auto mb-4" />
                    <p className="text-sm font-medium text-[#2F1B2C] mb-1">Click to upload or drag and drop</p>
                    <p className="text-xs text-gray-500 mb-4">Max file size: 10MB per file</p>
                    <p className="text-[11px] text-gray-400">Supported formats: .pdf .txt .docx .md .xlsx .xls</p>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    accept=".pdf,.txt,.docx,.md,.xlsx,.xls"
                  />
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => setShowUploadModal(false)}
                    className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-[#2F1B2C] tracking-tight">Knowledge base</h1>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 pb-4 border-b border-gray-200 text-xs text-gray-500">
            <span>Uploaded <span className="text-gray-800 font-medium">{uploadedCount}</span></span>
            <span className="text-gray-300">|</span>
            <span>Scraped <span className="text-gray-800 font-medium">{scrapedCount}</span></span>
            <span className="text-gray-300">|</span>
            <span>Active <span className="text-gray-800 font-medium">{activeCount}</span></span>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 py-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="relative w-full md:w-72">
              <FaSearch className="absolute left-0 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search documents"
                className="w-full pl-5 pr-2 py-1.5 border-b border-gray-300 focus:border-[#6E4466] text-sm outline-none bg-transparent transition"
              />
            </div>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#6E4466] transition"
            >
              <FaFilter className="text-xs" />
              Filters
              {isFilterOpen ? <FaChevronUp className="text-[10px]" /> : <FaChevronDown className="text-[10px]" />}
            </button>
          </div>

          <div className="flex items-center gap-5">
            <button
              onClick={() => {
                setWebsiteUrl("");
                setSitemapUrls([]);
                setSelectedUrls([]);
                setLoadingUrls(false);
                setShowSitemapModal(true);
              }}
              className="flex items-center gap-2 text-sm font-medium text-[#6E4466] hover:text-[#54334E] transition"
            >
              <FaGlobe className="text-xs" />
              Scrape website
            </button>

            <span className="w-px h-4 bg-gray-300" />

            {files.filter(f => f.status !== "deleted").length > 0 && (
              <div className="relative group">
                <button
                  onClick={handleResync}
                  disabled={resyncLoading}
                  className={`flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#6E4466] transition ${resyncLoading ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <FaSync className={`text-xs ${resyncLoading ? "animate-spin" : ""}`} />
                  {resyncLoading ? "Syncing…" : "Resync"}
                </button>
                <div className="absolute top-full right-0 mt-2 px-3 py-1.5 bg-[#2F1B2C] text-white text-[10px] whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-[9999]">
                  Refresh knowledge base index after adding documents
                </div>
              </div>
            )}

            <span className="w-px h-4 bg-gray-300" />

            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-[#6E4466] text-white hover:bg-[#54334E] transition"
            >
              <FaUpload className="text-xs" />
              Upload files
            </button>
          </div>
        </div>

        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden border-b border-gray-200"
            >
              <div className="flex flex-wrap items-end gap-6 py-4">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Status</label>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="border-b border-gray-300 focus:border-[#6E4466] outline-none text-sm py-1 bg-transparent"
                  >
                    <option value="all">All status</option>
                    <option value="active">Active</option>
                    <option value="processing">Processing</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">File type</label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="border-b border-gray-300 focus:border-[#6E4466] outline-none text-sm py-1 bg-transparent"
                  >
                    <option value="all">All types</option>
                    <option value="pdf">PDF</option>
                    <option value="txt">TXT</option>
                    <option value="md">Markdown</option>
                    <option value="csv">CSV</option>
                    <option value="xlsx">Excel</option>
                    <option value="docx">Word</option>
                  </select>
                </div>
                <button
                  onClick={() => {
                    setQuery("");
                    setStatusFilter("all");
                    setTypeFilter("all");
                  }}
                  className="text-sm text-[#6E4466] hover:text-[#54334E] transition pb-1"
                >
                  Clear filters
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {filteredFiles.length === 0 ? (
          <div className="py-16 text-center border-b border-gray-200">
            <FaBook className="text-3xl text-[#6E4466] mx-auto mb-4" />
            <h3 className="text-base font-semibold text-[#2F1B2C] mb-2">Your knowledge base is empty</h3>
            <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
              Add documents or scrape pages from your website, so your AI agent can answer questions accurately
            </p>
            <div className="flex justify-center gap-6">
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-[#6E4466] text-white hover:bg-[#54334E] transition"
              >
                <FaUpload className="text-xs" />
                Upload files
              </button>
              <button
                onClick={() => {
                  setWebsiteUrl("");
                  setSitemapUrls([]);
                  setSelectedUrls([]);
                  setLoadingUrls(false);
                  setShowSitemapModal(true);
                }}
                className="flex items-center gap-2 px-5 py-2 text-sm font-medium border border-[#6E4466] text-[#6E4466] hover:bg-[#6E4466] hover:text-white transition"
              >
                <FaGlobe className="text-xs" />
                Scrape website
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between py-3 border-b border-gray-200">
              <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={paginatedFiles.length > 0 && paginatedFiles.every(f => selectedFiles.has(f.file_name))}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setSelectedFiles(prev => {
                      const newSet = new Set(prev);
                      if (checked) {
                        paginatedFiles.forEach(f => newSet.add(f.file_name));
                      } else {
                        paginatedFiles.forEach(f => newSet.delete(f.file_name));
                      }
                      return newSet;
                    });
                  }}
                  className="w-3.5 h-3.5 rounded-none border-gray-400 text-[#6E4466] focus:ring-0"
                />
                Select all on page
              </label>
              {selectedFiles.size > 0 && (
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="flex items-center gap-1.5 text-xs font-medium text-red-600 hover:text-red-700 transition"
                >
                  <FaTrash className="text-[10px]" />
                  Delete selected ({selectedFiles.size})
                </button>
              )}
            </div>

            {paginatedFiles.map((file) => (
              <FileRow
                key={file.file_id}
                file={file}
                isSelected={selectedFiles.has(file.file_name)}
                onSelect={(checked) => {
                  setSelectedFiles(prev => {
                    const newSet = new Set(prev);
                    if (checked) newSet.add(file.file_name);
                    else newSet.delete(file.file_name);
                    return newSet;
                  });
                }}
                onView={handleFileClick}
                onDelete={(f) => {
                  setSelectedFiles(new Set([f.file_name]));
                  setShowDeleteModal(true);
                }}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}

        {filteredFiles.length > 0 && renderPagination()}
      </div>
    </div>
  );
};

export default KnowledgeAfterOnboard;